import Link from "next/link";
import { Card, CardContent } from "@/components/ui/card";
import { Sparkline } from "@/components/dashboard/sparkline";

// A Home stat card: round icon on the left; label, big value (with an optional
// up/down trend) and a small caption beside it; then a sparkline or, for
// storage, a usage bar underneath.

export type Trend = { pct: number; title: string };

function TrendBadge({ trend }: { trend: Trend }) {
  const up = trend.pct >= 0;
  return (
    <span
      title={trend.title}
      className={`text-xs font-semibold ${up ? "text-accent-green" : "text-accent-red"}`}
    >
      {up ? "↑" : "↓"} {Math.abs(trend.pct)}%
    </span>
  );
}

export function StatCard({
  label,
  value,
  sub,
  subTitle,
  trend,
  iconBg,
  icon,
  spark,
  bar,
  href,
}: {
  label: string;
  value: string;
  sub: string;
  subTitle?: string;
  trend?: Trend | null;
  iconBg: string;
  icon: React.ReactNode;
  spark?: { values: number[]; color: string; scale?: "zero" | "fit" };
  // 0–100: draws a usage bar instead of a sparkline.
  bar?: { percent: number; color: string } | null;
  // Makes the whole card a link to that page.
  href?: string;
}) {
  return (
    <Card className={href ? "relative h-full transition-shadow hover:shadow-md" : "h-full"}>
      {href && <Link href={href} aria-label={`${label}: open`} title={subTitle} className="absolute inset-0 z-0 rounded-xl" />}
      <CardContent className="pt-5">
        <div className="flex items-center gap-4">
          {/* Fixed-size circle with the icon dead centre. */}
          <div
            className={`flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-full ${iconBg}`}
          >
            {icon}
          </div>
          <div className="min-w-0">
            <div className="text-[15px] font-semibold leading-tight">{label}</div>
            <div className="mt-0.5 flex flex-wrap items-baseline gap-x-2">
              <span className="text-2xl font-bold leading-tight">{value}</span>
              {trend && <TrendBadge trend={trend} />}
            </div>
            <div className="text-xs text-text-tertiary" title={subTitle}>
              {sub}
            </div>
          </div>
        </div>
        {bar ? (
          <div className="mt-3.5 h-2 w-full overflow-hidden rounded-full bg-border">
            <div
              className="h-full rounded-full"
              style={{ width: `${Math.max(2, Math.min(100, bar.percent))}%`, backgroundColor: bar.color }}
            />
          </div>
        ) : (
          spark && <Sparkline values={spark.values} color={spark.color} scale={spark.scale} />
        )}
      </CardContent>
    </Card>
  );
}
